import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGlobalProperty } from "../context/GlobalPropertyContext";
import { useCart } from "../context/CartContext";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/style_header.css";
import { API } from "../api";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

function SubMenu({ isOpen, children, onMouseEnter, onMouseLeave, isMobile }) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuRef.current) return;

    if (isMobile) {
      // Animation mobile pour les sous-menus - slide depuis la droite
      if (isOpen) {
        gsap.set(menuRef.current, { display: "block" });
        gsap.fromTo(menuRef.current, 
          {
            autoAlpha: 0,
            x: -20,
          },
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.3,
            ease: "power2.out",
            pointerEvents: "auto",
          }
        );
      } else {
        gsap.to(menuRef.current, {
          autoAlpha: 0,
          x: -20,
          duration: 0.2,
          ease: "power2.in",
          pointerEvents: "none",
          onComplete: () => {
            if (menuRef.current) {
              gsap.set(menuRef.current, { display: "none" });
            }
          },
        });
      }
    } else {
      // Animation desktop - classique
      gsap.to(menuRef.current, {
        autoAlpha: isOpen ? 1 : 0,
        y: isOpen ? 0 : -10,
        duration: 0.25,
        ease: "power2.out",
        pointerEvents: isOpen ? "auto" : "none",
      });
    }
  }, [isOpen, isMobile]);

  return (
    <div 
      ref={menuRef} 
      className="sub-menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {children}
    </div>
  );
}


export default function Header() {
  const { user, loading } = useAuth();
  const { globalProperties, loading: globalLoading } = useGlobalProperty();
  const { totals } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const sections = ["evenement", "carrousel", "horaire"];
  const navRefs = useRef({});

  const leftRef = useRef(null);
  const navContainerRef = useRef(null);
  const headerRef = useRef(null);

  const [openMenu, setOpenMenu] = useState(null);
  const [activeSection, setActiveSection] = useState("evenement");
  const menuCloseTimeoutRef = useRef(null);
  const isTouch = "ontouchstart" in window;
  const isAuthenticated = Boolean(user);
  const isAdmin = user?.frontend_permissions?.admin_panel?.access?.allowed === true;

  const userMenuKey = isAuthenticated ? "user" : "unconnected";
  const adminMenuKey = "admin";

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 480);
  
  const toggleMobileMenu = () => {
    setIsMobileOpen(prev => !prev);
  };

  // Gestion des animations du menu mobile avec GSAP
  useEffect(() => {
    if (!navContainerRef.current) return;

    if (isMobileOpen) {
      // Ouvrir le menu mobile
      gsap.to(navContainerRef.current, {
        left: 0,
        duration: 0.4,
        ease: "power2.out",
        clearProps: "none",
      });
    } else {
      // Fermer le menu mobile
      gsap.to(navContainerRef.current, {
        left: "-50%",
        duration: 0.4,
        ease: "power2.out",
        clearProps: "none",
      });
    }
  }, [isMobileOpen]);

  // Fermeture du menu mobile lors de la navigation
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Détection de changement de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const newIsMobileView = window.innerWidth <= 480;
      setIsMobileView(newIsMobileView);
      
      // Fermer le menu si on passe en desktop
      if (!newIsMobileView && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileOpen]);

  // Fonction pour fermer le menu avec délai
  const closeMenuWithDelay = (menuKey) => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current);
    }
    menuCloseTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 200);
  };

  // Fonction pour annuler la fermeture du menu
  const cancelMenuClose = () => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current);
    }
  };

  const handleUserBoxClick = () => {
    if (isMobileView) {
      // Sur mobile : on ouvre/ferme le menu au clic
      setOpenMenu(openMenu === userMenuKey ? null : userMenuKey);
      return;
    }

    if (isAuthenticated) navigate("/account");
    else navigate("/auth", { state: { defaultForm: "signup" } });
  };


  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null);
      return;
    }
    const initScrollTriggers = () => {
      const sectionsExist = sections.every(section => document.getElementById(section));
      
      if (!sectionsExist) {
        setTimeout(initScrollTriggers, 100);
        return;
      }

      ScrollTrigger.getAll().forEach(t => t.kill());

      const triggers = sections.map((section) => {
        return ScrollTrigger.create({
          trigger: `#${section}`,
          start: "top center",
          end: "bottom center",
          onEnter: () => setActiveSection(section),
          onEnterBack: () => setActiveSection(section),
        });
      });

      const scrollY = window.scrollY;
      let currentSection = "evenement";
      
      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollY;
          const elementBottom = elementTop + rect.height;
          const centerScreen = scrollY + window.innerHeight / 2;
          
          if (centerScreen >= elementTop && centerScreen <= elementBottom) {
            currentSection = section;
          }
        }
      });
      
      setActiveSection(currentSection);
      ScrollTrigger.refresh();
    };

    initScrollTriggers();

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [location.pathname]);

  const goToSection = (id) => {
    gsap.to(window, {
      duration: 1,
      scrollTo: `#${id}`,
      ease: "power2.out",
    });
  };

  const goHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => goToSection("hero"), 300);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.timeline({
        defaults: {
          duration: 0.8,
          ease: "power3.out",
        },
      })
      .from(leftRef.current, {
        x: -60,
        y: -60,
        opacity: 0,
      })
      .from(navContainerRef.current, {
        x: 60,
        y: -60,
        opacity: 0,
      }, "-=0.5");
    });

    return () => ctx.revert();
  }, []);

  const { setUser } = useAuth();
  const handleLogout = async () => {
    const { success, error } = await API.post("auth/logout");
    if (success) setUser(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Vérifier si le clic est en dehors des menus
      const isClickInsideMenu = e.target.closest(".nav-item") || 
                                e.target.closest(".user-box") || 
                                e.target.closest(".sub-menu");
      
      if (!isClickInsideMenu) {
        setOpenMenu(null);
      }
    };

    const handleScroll = () => setOpenMenu(null);

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleScroll);
    
    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);


  const isAdminRoute = location.pathname === "/admin";

  return (
    <header className={`header ${isAdminRoute ? "admin-route" : ""}`} ref={headerRef}>
        <div className="header-left" ref={leftRef}>
          {location.pathname !== "/admin" && (
            <img src={`${API.API_LINK}/file/pictures/logo/source/logo_l_atelier_floral180x260.png`} alt="Logo" className="logo" onClick={goHome}/>
          )}
        </div>
      

      <div className="burger-btn" onClick={toggleMobileMenu}>
        <span className={`line line1 ${isMobileOpen ? "open" : ""}`}></span>
        <span className={`line line2 ${isMobileOpen ? "open" : ""}`}></span>
        <span className={`line line3 ${isMobileOpen ? "open" : ""}`}></span>
      </div>
      <nav className="nav" ref={navContainerRef}>
        {location.pathname === "/" ? (
          sections.map((section) => (
            <button 
              key={section} 
              ref={(el) => (navRefs.current[section] = el)} 
              onClick={() => goToSection(section)} 
              className={`nav-link ${activeSection === section ? "active" : ""}`}>
                <span>{section}</span>
            </button>
          ))
        ) : (<button className="nav-link" onClick={goHome}><span>Accueil</span></button>)}

        {globalProperties?.configuration?.is_shop_active && (<button className={`nav-link ${ location.pathname === "/boutique" ? "active" : "" }`} onClick={() => navigate("/boutique")}><span>Boutique</span></button>)}

        {/* Bouton Panier */}
        {globalProperties?.configuration?.is_shop_active && (
          <button 
            className={`nav-link cart-btn ${location.pathname === "/panier" ? "active" : ""}`} 
            onClick={() => navigate("/panier")}
          >
            <ion-icon name="cart-outline"></ion-icon>
            {totals.itemCount > 0 && (
              <span className="cart-badge">{totals.totalQuantity > 99 ? "99+" : totals.totalQuantity}</span>
            )}
          </button>
        )}

        {isAdmin && (
          <div
            className="nav-item"
            onMouseEnter={() => !isTouch && !isMobileView && (setOpenMenu(adminMenuKey), cancelMenuClose())}
            onMouseLeave={() => !isTouch && !isMobileView && closeMenuWithDelay(adminMenuKey)}>
            <button 
              className={`nav-link ${location.pathname === "/admin" ? "active" : ""}`} 
              onClick={() => isMobileView ? setOpenMenu(openMenu === adminMenuKey ? null : adminMenuKey) : (isTouch ? setOpenMenu(openMenu === adminMenuKey ? null : adminMenuKey) : navigate("/admin"))}>
              <span>Admin</span>
            </button>
            <SubMenu 
              isMobile={isMobileView}
              isOpen={openMenu === adminMenuKey}
              onMouseEnter={() => !isTouch && cancelMenuClose()}
              onMouseLeave={() => !isTouch && closeMenuWithDelay(adminMenuKey)}>
              <button onClick={(e) => { e.stopPropagation(); navigate("/admin", { state: { adminItem: "users" } }); setOpenMenu(null); }}>Gérer utilisateurs</button>
              <button onClick={(e) => { e.stopPropagation(); navigate("/admin", { state: { adminItem: "dashboard" } }); setOpenMenu(null); }}>Tableau de bord</button>
              <button onClick={(e) => { e.stopPropagation(); navigate("/admin", { state: { adminItem: "confGeneral" } }); setOpenMenu(null); }}>Paramètres</button>
            </SubMenu>
          </div>
        )}

        <div className="user-box" 
          onMouseEnter={() => !isTouch && !isMobileView && (setOpenMenu(userMenuKey), cancelMenuClose())}
          onMouseLeave={() => !isTouch && !isMobileView && closeMenuWithDelay(userMenuKey)}
          onClick={handleUserBoxClick}
        >
          {!user ? (
            <>
              <img src={ `${API.API_LINK}/file/pictures/avatars/source/ico-connexion45x45.png`} className="avatar" alt="profile" />
              <div className="user-info"><span className="auth-link">Login / Register</span></div>
            </>
          ) : (
            <>
              <img src={user?.image_source === "local" && user?.image_name && user?.image_extension
                ? `${API.API_LINK}/file/pictures/avatars/source/` + user.image_name + "." + user.image_extension
                : user?.image_source === "web" && user?.image_name ? user.image_name
                : `${API.API_LINK}/file/pictures/avatars/source/ico-connexion45x45.png`
              } className="avatar" alt="profile" />
              <div className="user-info"><span>{user.first_name} {user.last_name}</span><small>{user.role_slug}</small></div>
            </>
          )}

          {isAuthenticated ? (
            <SubMenu 
              isMobile={isMobileView}
              isOpen={openMenu === "user"}
              onMouseEnter={() => !isTouch && cancelMenuClose()}
              onMouseLeave={() => !isTouch && closeMenuWithDelay(userMenuKey)}>
              <button onClick={(e) => { e.stopPropagation(); navigate("/profil"); setOpenMenu(null); }}>Profil</button>
              <button className="danger" onClick={(e) => { e.stopPropagation(); handleLogout(); setOpenMenu(null); }}>Déconnexion</button>
            </SubMenu>
          ) : (
            <SubMenu 
              isMobile={isMobileView}
              isOpen={openMenu === "unconnected"}
              onMouseEnter={() => !isTouch && cancelMenuClose()}
              onMouseLeave={() => !isTouch && closeMenuWithDelay(userMenuKey)}>
              <button onClick={(e) => { e.stopPropagation(); navigate("/auth", { state: { defaultForm: "signin" }, replace: true }); }}>Connexion</button>
              <button onClick={(e) => { e.stopPropagation(); navigate("/auth", { state: { defaultForm: "signup" }, replace: true }); }}>Inscription</button>
            </SubMenu>
          )}
        </div>
      </nav>
    </header>
    
  );
}